import React, { useState } from "react"
import styled from "styled-components"
import { IconButton } from "@mui/material"
import { MdClose, MdWarningAmber } from "react-icons/md"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import {
    MobergButton,
    MobergButtonShape,
    MobergButtonVariant,
    MobergTheme,
} from "../../../../Moberg"


const Container = styled.div`
    background: var(--color-surface-primary);
    color: var(--color-text-primary);
    padding: 24px 28px;
    border-radius: 8px;
    width: 480px;
    max-width: 92vw;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
`

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
`

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`

const WarningIcon = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(244, 67, 54, 0.12);
    color: #F44336;
    flex-shrink: 0;
`

const Title = styled.h2`
    margin: 0;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
`

const BodyText = styled.p`
    margin: 0 0 12px 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-text-primary);
`

const MetaBlock = styled.div`
    background: var(--color-hover-neutral);
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
    font-size: 12.5px;
    color: var(--text-secondary-color);
    word-break: break-all;
`

const MetaLabel = styled.div`
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 2px;
`

const Disclaimer = styled.div`
    font-size: 12.5px;
    font-style: italic;
    color: var(--text-secondary-color);
    margin-bottom: 18px;
`

const ButtonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`


const CancelConfirmModal = ({ row, onConfirm }) => {
    const { close } = useModalProvider()
    const [submitting, setSubmitting] = useState(false)

    // A PENDING job has not started converting, so there is no partial work to
    // lose. Saying "progress will be discarded" about a job sitting in the queue
    // reads as a bigger consequence than it is.
    const inProgress = row?.status === "IN_PROGRESS"

    const bodyText = inProgress
        ? "This will stop the conversion. Any work done so far is discarded and no file is produced, so the export has to be started again from the beginning."
        : "This will remove the export from the queue before it starts. No file is produced, so the export has to be started again."

    const progressPct = typeof row?.progress === "number"
        ? Math.round(row.progress * 100)
        : null

    const handleConfirm = async () => {
        setSubmitting(true)
        try {
            await onConfirm()
        } finally {
            close()
        }
    }

    return (
        <Container>
            <HeaderRow>
                <HeaderLeft>
                    <WarningIcon>
                        <MdWarningAmber size={22} />
                    </WarningIcon>
                    <Title>Cancel this export?</Title>
                </HeaderLeft>
                <IconButton
                    onClick={close}
                    aria-label="Close"
                    size="small"
                    disabled={submitting}
                    sx={{ color: "var(--text-secondary-color)", marginRight: "-8px" }}
                >
                    <MdClose size={22} />
                </IconButton>
            </HeaderRow>

            <BodyText>{bodyText}</BodyText>

            <MetaBlock>
                <MetaLabel>
                    Job #{row?.id} ({row?.patient_identifier || row?.patient_id || "Unknown patient"})
                </MetaLabel>
                {inProgress && progressPct !== null && (
                    <div>{progressPct}% converted so far</div>
                )}
            </MetaBlock>

            <Disclaimer>
                Cancelling takes effect at the next safe point in the conversion, so the
                export may take a moment to settle to Cancelled.
            </Disclaimer>

            <ButtonRow>
                {/* The dismiss action is the safe one, so it holds focus: pressing
                    Enter on this dialog must never be what kills a running export.
                    It is deliberately not labelled "Cancel", which would be asking
                    whether the user wants to cancel the cancel. */}
                <MobergButton
                    variant={MobergButtonVariant.OUTLINE}
                    theme={MobergTheme.GRAY}
                    shape={MobergButtonShape.RECTANGLE}
                    onClick={close}
                    disabled={submitting}
                    autoFocus
                >
                    {inProgress ? "Keep running" : "Keep in queue"}
                </MobergButton>
                <MobergButton
                    variant={MobergButtonVariant.FILLED}
                    theme={MobergTheme.RED}
                    shape={MobergButtonShape.RECTANGLE}
                    onClick={handleConfirm}
                    disabled={submitting}
                >
                    {submitting ? "Cancelling…" : "Cancel export"}
                </MobergButton>
            </ButtonRow>
        </Container>
    )
}

export default CancelConfirmModal
